// The shared artwork pipeline behind BOTH design previews (W4 phase 2).
//
// Lifted verbatim out of components/PreviewCanvas.tsx so the artboard
// visualiser and the on-garment photo preview (OnGarmentPreview) derive
// the SAME artwork from a spot draft: optional in-browser background
// removal, the pet/kid/person drawing filter, the varsity arched-text
// flags, font + thread colour resolution, and the rendered image's
// measured aspect ratio. PreviewCanvas keeps its layout maths, rulers,
// zoom and garment-colour stage; this hook owns only "what artwork/text
// should the preview draw".
//
// Behaviour contract: byte-identical to the pre-refactor PreviewCanvas.
// Any change here shows up in both previews - treat it as shared
// infrastructure, not a component helper.

import * as React from "react";
import type { Config, Font, Placement } from "../types";
import type { EmbroideryDraft } from "../sections/PlacementSection";
import { flowKindFor } from "../flow";
import { wordArtPresetById } from "../wordart/presets";
import type { WordArtPreset } from "../wordart/presets";
import { uploadedFileUrl } from "../api";
import { renderDrawing, trimTransparent } from "./drawingFilter";

export type PreviewArtwork = {
  /** The spot's resolved placement row, or null. */
  placement: Placement | null;
  mode: EmbroideryDraft["mode"];
  /** The spot's picked size (mm). THE authority - never mutated here. */
  sizeMm: number;
  /** Trimmed raw text. */
  text: string;
  /** Text as it will stitch (uppercased for caps-only faces). */
  displayText: string;
  isPdf: boolean;
  hasUpload: boolean;
  /** The raw uploaded image URL (null for PDFs / no upload). Drives the
   *  remove-background toggle's visibility. */
  imageUrl: string | null;
  removeBg: boolean;
  /** True while the background-removal model is running. */
  bgBusy: boolean;
  /** The artwork URL the preview should draw - drawing-filtered and/or
   *  background-removed when those apply, else the original. */
  finalArtUrl: string | null;
  /** Measured aspect ratio (w/h) of finalArtUrl, null until loaded. */
  imgAspect: number | null;
  wantsDrawing: boolean;
  /** Caveat line for the drawing flows, or null. */
  drawingNote: string | null;
  /** Varsity / college template: arched text over the design. */
  isVarsity: boolean;
  hasText: boolean;
  hasArt: boolean;
  /** Nothing to draw yet (no spot / no mode / no content). */
  isEmpty: boolean;
  font: Font | null;
  textColour: string;
  /** Varsity outline thread hex, or null until picked. */
  outlineColour: string | null;
  /** Word Art (2026-07): resolved preset for this spot, or null. When set,
   *  the preview renders the Word Art treatment instead of plain text. */
  wordArtPreset: WordArtPreset | null;
};

export function usePreviewArtwork({
  config,
  spot,
  workerUrl,
  active,
  dark,
}: {
  config: Config;
  /** The spot being previewed (live draft or saved spot), or null. */
  spot: EmbroideryDraft | null;
  workerUrl: string;
  /** True while the preview is actually visible. Heavy work (AI
   *  background removal, drawing filter) is deferred until then. */
  active: boolean;
  /** True when the surface behind the artwork is dark - drives the
   *  default line-drawing thread contrast (white on dark). */
  dark: boolean;
}): PreviewArtwork {
  const [imgAspect, setImgAspect] = React.useState<number | null>(null);

  const placement = spot?.placementSlug
    ? config.placements.find((p) => p.slug === spot.placementSlug) ?? null
    : null;

  const mode = spot?.mode ?? null;
  const sizeMm = spot?.sizeMm || 80;
  const text = (spot?.text ?? "").trim();

  const mime = (spot?.uploadMimeType ?? "").toLowerCase();
  const fname = (spot?.uploadFilename ?? "").toLowerCase();
  const isPdf = mime.includes("pdf") || fname.endsWith(".pdf");
  const hasUpload = !!spot?.uploadStagingKey;
  const imageUrl =
    hasUpload && !isPdf && spot?.uploadStagingKey
      ? uploadedFileUrl(workerUrl, spot.uploadStagingKey)
      : null;

  // Optional in-browser AI background removal — a preview aid only. When
  // the customer ticked "Remove the background", run a segmentation
  // model (loaded on demand from a CDN) on the artwork and render the
  // transparent result. Any failure falls back silently to the original.
  const removeBg = !!spot?.removeBg;
  const [bgUrl, setBgUrl] = React.useState<string | null>(null);
  const [bgBusy, setBgBusy] = React.useState(false);
  // Cache the processed result so reopening the drawer (or toggling the
  // option) is instant. Revoked on unmount.
  const bgCache = React.useRef<{ src: string; out: string } | null>(null);
  React.useEffect(() => {
    return () => {
      if (bgCache.current) URL.revokeObjectURL(bgCache.current.out);
    };
  }, []);
  React.useEffect(() => {
    if (!removeBg || !imageUrl) {
      setBgUrl(null);
      setBgBusy(false);
      return;
    }
    // Reuse a cached result for this exact image — no rework, instant.
    if (bgCache.current && bgCache.current.src === imageUrl) {
      setBgUrl(bgCache.current.out);
      setBgBusy(false);
      return;
    }
    // Defer the heavy ML work until the drawer is actually opened, so a
    // closed preview (e.g. straight after a page refresh) costs nothing.
    if (!active) return;
    let cancelled = false;
    setBgBusy(true);
    (async () => {
      try {
        const cdn = "https://cdn.jsdelivr.net";
        const mod = (await import(
          /* @vite-ignore */ `${cdn}/npm/@imgly/background-removal@1/+esm`
        )) as { removeBackground: (input: string) => Promise<Blob> };
        const blob = await mod.removeBackground(imageUrl);
        if (cancelled) return;
        // Trim the transparent margins so text below sits flush.
        const trimmed = await trimTransparent(blob);
        if (cancelled) return;
        const out = URL.createObjectURL(trimmed);
        if (bgCache.current && bgCache.current.src !== imageUrl) {
          URL.revokeObjectURL(bgCache.current.out);
        }
        bgCache.current = { src: imageUrl, out };
        setBgUrl(out);
      } catch {
        if (!cancelled) setBgUrl(null);
      } finally {
        if (!cancelled) setBgBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [removeBg, imageUrl, active]);

  // The artwork URL the canvas actually draws — the background-removed
  // version when it's ready, else the original.
  const artUrl = removeBg && bgUrl ? bgUrl : imageUrl;

  // Pet + kid + person all run the single-design "drawing" flow: upload
  // a photo or drawing, optional text under it, one thread colour. They
  // carry that optional text alongside the upload even though mode
  // stays "logo", and all three turn the artwork into a drawing
  // impression. Person flow = line-drawing of a real person, with the
  // same optional coloured + filled upgrade pet/kid have.
  // Phase 70: source the flow kind from the FlowBlueprint instead of the
  // legacy template column (falls back to the column during migration).
  const flowKind = flowKindFor(config);
  const isPetFlow = flowKind === "pet";
  const isKidFlow = flowKind === "kid";
  const isPersonFlow = flowKind === "person";
  const isSingleDesignFlow = isPetFlow || isKidFlow || isPersonFlow;
  // Phase 67h: a placement may bypass the template locks. The pet-
  // varsity BACK spot uses this so its preview renders as a standard
  // flat-text design — no arched text, no outline stroke, no forced
  // line/coloured drawing style.
  const bypassLocks = !!placement?.bypassTemplateLocks;
  // Varsity / college style: a pet flow that locks text above the
  // design. The preview renders that text arched over the artwork.
  const isVarsity =
    isPetFlow &&
    config.product.lockedArrangement === "above" &&
    !bypassLocks;
  const wantsText =
    mode === "text" || mode === "both" || isSingleDesignFlow;
  const wantsArt = mode === "logo" || mode === "both";
  const hasText = wantsText && text.length > 0;
  const hasArt = wantsArt && hasUpload;
  const isEmpty = !spot || !mode || (!hasText && !hasArt);

  const font: Font | null = spot?.fontId
    ? config.fonts.find((f) => f.id === spot.fontId) ?? null
    : null;
  const wordArtPreset = wordArtPresetById(spot?.wordArtStyleId);
  const displayText = font?.forceUppercase ? text.toUpperCase() : text;
  const textColour =
    config.threads.find((t) => t.code === spot?.textThreadCodes[0])?.hex ??
    "#15131a";
  // Varsity / college only: the thread colour for the stitched outline
  // around the arched text. Null until the customer picks one — no
  // stroke is drawn until then.
  const outlineColour =
    config.threads.find((t) => t.code === spot?.outlineThreadCode)?.hex ??
    null;

  // Pet flow: turn the uploaded photo into a rough drawing impression —
  // a line drawing, or a coloured + filled drawing — matching the style
  // the customer picked. A preview aid only; the artists hand-draw the
  // final artwork from the customer's photo.
  // Phase 67h: bypass placements ignore the locked addon style and use
  // the spot's own choice (back of pet-varsity behaves like a regular
  // spot).
  const lockedStyle = bypassLocks
    ? null
    : config.product.lockedAddonStyle ?? null;
  const colouredStyle = lockedStyle
    ? lockedStyle === "coloured"
    : !!spot?.addonStyleColoured;
  const wantsDrawing = isSingleDesignFlow && hasArt && !isPdf;
  // The chosen thread colour — a line drawing is rendered in it.
  const petThreadHex =
    config.threads.find(
      (t) => t.code === (spot?.logoThreadCodes[0] ?? spot?.textThreadCodes[0]),
    )?.hex ?? null;
  // Phase 87: line drawings stitch in the chosen thread. Until the
  // customer picks one, default the PREVIEW line colour to contrast the
  // garment - white on a dark garment, near-black on a light one - so the
  // design is always visible in the visualiser (mirrors the real-world
  // "dark garment => white thread, light garment => black thread" rule).
  // An explicit thread pick always wins.
  const previewLineHex = petThreadHex ?? (dark ? "#ffffff" : "#1a1a1a");
  const [drawnUrl, setDrawnUrl] = React.useState<string | null>(null);
  // Cache by image + style so flipping line / coloured is instant.
  const drawCache = React.useRef<Map<string, string>>(new Map());
  React.useEffect(
    () => () => {
      drawCache.current.forEach((u) => URL.revokeObjectURL(u));
      drawCache.current.clear();
    },
    [],
  );
  React.useEffect(() => {
    if (!wantsDrawing || !artUrl) {
      setDrawnUrl(null);
      return;
    }
    const key = `${artUrl}|${
      colouredStyle ? "c" : "l|" + previewLineHex
    }`;
    const cached = drawCache.current.get(key);
    if (cached) {
      setDrawnUrl(cached);
      return;
    }
    if (!active) return;
    let cancelled = false;
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      renderDrawing(im, colouredStyle, previewLineHex)
        .then((blob) => {
          if (cancelled || !blob) return;
          const url = URL.createObjectURL(blob);
          drawCache.current.set(key, url);
          setDrawnUrl(url);
        })
        .catch(() => {
          /* fall back to the plain photo */
        });
    };
    im.onerror = () => {
      /* fall back to the plain photo */
    };
    im.src = artUrl;
    return () => {
      cancelled = true;
    };
  }, [wantsDrawing, artUrl, colouredStyle, previewLineHex, active]);

  // The artwork the canvas finally draws — the drawing-styled version
  // for the pet flow, else the (possibly background-removed) photo.
  const finalArtUrl = wantsDrawing && drawnUrl ? drawnUrl : artUrl;
  const drawingNote = wantsDrawing
    ? "A quick preview — our artists hand-draw the final from your photo."
    : null;

  // Measure the aspect ratio of the image actually drawn (after any
  // background removal / drawing filter, both of which are trimmed to
  // their content) so the layout — and text placed below it — fits the
  // real artwork with no transparent gap.
  React.useEffect(() => {
    if (!finalArtUrl) {
      setImgAspect(null);
      return;
    }
    let cancelled = false;
    const im = new Image();
    im.onload = () => {
      if (!cancelled && im.naturalWidth > 0 && im.naturalHeight > 0) {
        setImgAspect(im.naturalWidth / im.naturalHeight);
      }
    };
    im.src = finalArtUrl;
    return () => {
      cancelled = true;
    };
  }, [finalArtUrl]);

  return {
    placement,
    mode,
    sizeMm,
    text,
    displayText,
    isPdf,
    hasUpload,
    imageUrl,
    removeBg,
    bgBusy,
    finalArtUrl,
    imgAspect,
    wantsDrawing,
    drawingNote,
    isVarsity,
    hasText,
    hasArt,
    isEmpty,
    font,
    textColour,
    outlineColour,
    wordArtPreset,
  };
}
